import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import AuthForm from './components/AuthForm';
import SocialAuthButtons from './components/SocialAuthButtons';
import TrustSignals from './components/TrustSignals';
import RadioWaveAnimation from './components/RadioWaveAnimation';
import FrequencyVisualizer from './components/FrequencyVisualizer';
import { AUTH_MODES, MOCK_CREDENTIALS, ERROR_MESSAGES, STORAGE_KEYS, PLATFORM_FEATURES } from '../../constants/authConstants';

const Authentication = () => {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState(AUTH_MODES?.LOGIN);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormActive, setIsFormActive] = useState(false);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    setAuthError('');
  }, [authMode]);

  const handleFormSubmit = (formData) => {
    setIsLoading(true);
    setAuthError('');

    setTimeout(() => {
      if (authMode === AUTH_MODES?.LOGIN) {
        if (
          formData?.email === MOCK_CREDENTIALS?.login?.email &&
          formData?.password === MOCK_CREDENTIALS?.login?.password
        ) {
          localStorage.setItem(STORAGE_KEYS?.IS_AUTHENTICATED, 'true');
          localStorage.setItem(STORAGE_KEYS?.USER_EMAIL, formData?.email);
          navigate('/main-dashboard');
        } else {
          setAuthError(ERROR_MESSAGES?.LOGIN_FAILED);
          setIsLoading(false);
        }
      } else {
        localStorage.setItem(STORAGE_KEYS?.IS_AUTHENTICATED, 'true');
        localStorage.setItem(STORAGE_KEYS?.USER_EMAIL, formData?.email);
        localStorage.setItem(STORAGE_KEYS?.USERNAME, formData?.username);
        navigate('/main-dashboard');
      }
    }, 1500);
  };

  const handleSocialAuth = (provider) => {
    setIsLoading(true);
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEYS?.IS_AUTHENTICATED, 'true');
      localStorage.setItem(STORAGE_KEYS?.AUTH_PROVIDER, provider);
      navigate('/main-dashboard');
    }, 1000);
  };

  const handleSwitchMode = () => {
    setAuthMode(prev => prev === AUTH_MODES?.LOGIN ? AUTH_MODES?.REGISTER : AUTH_MODES?.LOGIN);
    setIsFormActive(false);
  };

  return (
    <>
      <Helmet>
        <title>Аутентификация - Radio Co Im</title>
        <meta name="description" content="Войдите в Radio Co Im - платформу для аутентичного голоса и прямых эфиров без редакторов" />
      </Helmet>
      <div className="authentication-page min-h-screen bg-background flex flex-col">
        <div className="auth-background-animation fixed inset-0 pointer-events-none overflow-hidden opacity-30">
          <div className="absolute top-0 left-0 w-full h-32">
            <RadioWaveAnimation isActive={isFormActive} />
          </div>
          <div className="absolute bottom-0 left-0 w-full h-24">
            <FrequencyVisualizer isActive={isFormActive} />
          </div>
        </div>
        <div className="auth-content flex-1 flex items-center justify-center p-4 relative z-10">
          <div className="auth-container w-full max-w-6xl mx-auto">
            <div className="auth-grid grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="auth-left-section hidden lg:block">
                <div className="brand-section mb-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="logo-icon w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon name="Radio" size={32} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-heading font-semibold text-foreground">
                        Radio Co Im
                      </h1>
                      <p className="text-sm text-muted-foreground font-caption">
                        Частоты присутствия
                      </p>
                    </div>
                  </div>

                  <div className="philosophy-text space-y-4 mb-8">
                    <p className="text-lg text-foreground/90 leading-relaxed">
                      Создайте безопасное эфирное пространство, где ваш подлинный голос звучит без шума, алгоритмов и редакторов.
                    </p>
                    <p className="text-base text-muted-foreground leading-relaxed">
                      Архитектура влияния через чистое звучание личности. Присоединяйтесь к сообществу аутентичных голосов.
                    </p>
                  </div>

                  <div className="platform-features space-y-3">
                    {PLATFORM_FEATURES?.map((feature, index) => (
                      <div key={index} className="feature-item flex items-center gap-3">
                        <div className="feature-icon w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon name={feature?.icon} size={16} color="var(--color-primary)" />
                        </div>
                        <span className="text-sm text-foreground">{feature?.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <TrustSignals />
              </div>

              <div className="auth-right-section">
                <div className="auth-card bg-card rounded-2xl shadow-soft-lg p-6 sm:p-8 border border-border">
                  <div className="auth-header mb-6 lg:hidden">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="logo-icon w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Radio" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h2 className="text-xl font-heading font-semibold text-foreground">
                          Radio Co Im
                        </h2>
                        <p className="text-xs text-muted-foreground font-caption">
                          Частоты присутствия
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="auth-mode-header mb-6">
                    <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                      {authMode === AUTH_MODES?.LOGIN ? 'Войти в эфир' : 'Создать свой канал'}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {authMode === AUTH_MODES?.LOGIN 
                        ? 'Продолжите вещание с того места, где остановились' 
                        : 'Начните транслировать свой аутентичный голос'
                      }
                    </p>
                  </div>

                  {authError && (
                    <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg" role="alert">
                      <p className="text-sm text-error whitespace-pre-line">{authError}</p>
                    </div>
                  )}

                  <div 
                    onFocus={() => setIsFormActive(true)}
                    onBlur={() => setIsFormActive(false)}
                  >
                    <AuthForm
                      mode={authMode}
                      onSubmit={handleFormSubmit}
                      onSwitchMode={handleSwitchMode}
                      isLoading={isLoading}
                    />
                  </div>

                  <SocialAuthButtons
                    onSocialAuth={handleSocialAuth}
                    isLoading={isLoading}
                  />

                  <div className="auth-footer mt-6 pt-6 border-t border-border">
                    <p className="text-xs text-center text-muted-foreground">
                      Входя на платформу, вы соглашаетесь с{' '}
                      <button className="text-primary hover:text-primary/80 transition-colors underline">
                        условиями использования
                      </button>
                      {' '}и{' '}
                      <button className="text-primary hover:text-primary/80 transition-colors underline">
                        политикой конфиденциальности
                      </button>
                    </p>
                  </div>
                </div>

                <div className="mobile-trust-signals lg:hidden mt-6">
                  <TrustSignals />
                </div>
              </div>
            </div>
          </div>
        </div>
        <footer className="auth-footer-bar py-4 border-t border-border bg-card/50 backdrop-blur-sm">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground text-center sm:text-left">
                © {new Date()?.getFullYear()} Radio Co Im. Все права защищены.
              </p>
              <div className="flex items-center gap-4">
                <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Помощь
                </button>
                <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Контакты
                </button>
                <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  О платформе
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Authentication;